import { z } from 'zod'

import { apiClient } from '../client'
import { isApiError } from '../errors'
import { canopyPath } from '../routes'

export const nestedCompanyProfileSchema = z.looseObject({
  id: z.string(),
  productDescription: z.string().nullish(),
  coreValueProposition: z.string().nullish(),
  primaryVertical: z.string().nullish(),
  buyerPersona: z.string().nullish(),
  aiNativeThreats: z.string().nullish(),
  coreWorkflows: z.array(z.string()).nullish(),
  namedCompetitors: z.array(z.string()).nullish(),
  marketKeywords: z.array(z.string()).nullish(),
})

export type NestedCompanyProfile = z.infer<typeof nestedCompanyProfileSchema>

export const companySchema = z
  .looseObject({
    id: z.string(),
    name: z.string(),
    website: z.string().nullish(),

    sector: z.string().nullish(),
    fund: z.string().nullish(),
    serentFund: z.string().nullish(),

    arrBand: z.string().nullish(),
    emailDomain: z.string().nullish(),

    profile: nestedCompanyProfileSchema.nullish(),

    entryDate: z.string().nullish(),

    status: z.string().nullish(),
    onboardingStep: z.number().int().nullish(),
    onboardedAt: z.string().nullish(),
    createdAt: z.string(),
    updatedAt: z.string(),

    aiBudget: z.unknown().nullish(),
    aiUsage: z.unknown().nullish(),
    governance: z.unknown().nullish(),
    monetizing: z.unknown().nullish(),
    ebitdaImpact: z.unknown().nullish(),
    aiLeader: z.unknown().nullish(),
    aiLeaderTitle: z.unknown().nullish(),
  })
  .transform(({ serentFund, ...company }) => ({
    ...company,
    fund: company.fund ?? serentFund ?? null,
  }))

export type Company = z.infer<typeof companySchema>

export const companyPageSchema = z.looseObject({
  items: z.array(companySchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
})

export type CompanyPage = z.infer<typeof companyPageSchema>

export const MAX_PAGE_SIZE = 500

export const FUNDS = ['I', 'II', 'III', 'IV', 'V'] as const

export const SECTORS = [
  'Education',
  'FinTech',
  'GovTech',
  'HR Tech',
  'Healthcare',
  'Hospitality & Travel',
  'IT & Cybersecurity',
  'Industrials',
  'Professional Services',
  'Real Estate',
] as const

export const STATUSES = ['active', 'monitoring', 'exited'] as const

export const ARR_BANDS = [
  '<$5M',
  '$5-10M',
  '$10-25M',
  '$25-50M',
  '$50-100M',
  '$100M+',
  'Confidential',
] as const

export type Sector = (typeof SECTORS)[number]

export type Fund = (typeof FUNDS)[number]

export const COMPANY_SORT_FIELDS = ['name', 'sector', 'fund', 'status', 'createdAt'] as const

export type CompanySortField = (typeof COMPANY_SORT_FIELDS)[number]

export interface ListCompaniesOptions {
  page?: number
  pageSize?: number

  sort?: CompanySortField
  direction?: 'asc' | 'desc'
  signal?: AbortSignal
}

export function listCompanies(options: ListCompaniesOptions = {}): Promise<CompanyPage> {
  const { page, pageSize, sort, direction, signal } = options

  return apiClient().get(canopyPath('/companies'), {
    schema: companyPageSchema,
    query: { page, pageSize, sort, direction },
    ...(signal ? { signal } : {}),
  })
}

export async function fetchAllCompanies(signal?: AbortSignal): Promise<Company[]> {
  const items: Company[] = []
  let page = 1

  for (;;) {
    const result = await listCompanies({ page, pageSize: MAX_PAGE_SIZE, ...(signal && { signal }) })
    items.push(...result.items)

    if (items.length >= result.total || result.items.length === 0) return items
    page += 1
  }
}

export function getCompany(companyId: string, signal?: AbortSignal): Promise<Company> {
  return apiClient().get(canopyPath(`/companies/${encodeURIComponent(companyId)}`), {
    schema: companySchema,
    ...(signal ? { signal } : {}),
  })
}

export const companyProfileSchema = z.looseObject({
  id: z.string(),
  companyId: z.string(),
  productDescription: z.string().nullish(),
  coreValueProposition: z.string().nullish(),
  primaryVertical: z.string().nullish(),
  buyerPersona: z.string().nullish(),
  coreWorkflows: z.array(z.string()).nullish(),
  namedCompetitors: z.array(z.string()).nullish(),
  marketKeywords: z.array(z.string()).nullish(),
  aiNativeThreats: z.string().nullish(),
  lastUpdatedByUserId: z.string().nullish(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type CompanyProfile = z.infer<typeof companyProfileSchema>

export async function getCompanyProfile(
  companyId: string,
  signal?: AbortSignal,
): Promise<CompanyProfile | null> {
  try {
    return await apiClient().get(
      canopyPath(`/companies/${encodeURIComponent(companyId)}/profile`),
      {
        schema: companyProfileSchema.nullable(),
        ...(signal ? { signal } : {}),
      },
    )
  } catch (error) {
    if (isMissingProfileError(error)) return null
    throw error
  }
}

function isMissingProfileError(error: unknown): boolean {
  if (!isApiError(error) || error.status !== 404) return false
  return /profile/i.test(error.message)
}

/**
 * Rows the server reports as deactivated by a delete.
 *
 * Every counter is `unknown`, deliberately. The keys document what the
 * endpoint reports, but nothing in the app reads them, and the live server
 * has already been seen to omit `checkins` and then to send it as something
 * other than an integer. Validating them only ever turned a delete the
 * server had *already committed* into a ResponseShapeError — the mutation's
 * onSuccess never ran, so the list never refreshed and the row sat there
 * until a reload. The HTTP status is the signal that a delete succeeded.
 */
export const deletionCountsSchema = z.looseObject({
  profiles: z.unknown().optional(),
  documents: z.unknown().optional(),
  snapshots: z.unknown().optional(),
  evidence: z.unknown().optional(),
  jobs: z.unknown().optional(),
  checkins: z.unknown().optional(),
})

export const deleteCompanyResultSchema = z.looseObject({
  success: z.boolean().optional(),
  deleted: deletionCountsSchema.optional(),
})

export type DeleteCompanyResult = z.infer<typeof deleteCompanyResultSchema>

export function deleteCompany(
  companyId: string,
  signal?: AbortSignal,
): Promise<DeleteCompanyResult> {
  return apiClient().delete(canopyPath(`/companies/${encodeURIComponent(companyId)}`), {
    schema: deleteCompanyResultSchema,
    ...(signal ? { signal } : {}),
  })
}

export const bulkDeleteResultSchema = z.looseObject({
  results: z.array(
    z.looseObject({
      companyId: z.string(),
      deleted: z.boolean(),
      deactivated: deletionCountsSchema.optional(),
    }),
  ),
  deletedCount: z.number().int(),
})

export type BulkDeleteResult = z.infer<typeof bulkDeleteResultSchema>

export function bulkDeleteCompanies(
  ids: readonly string[],
  signal?: AbortSignal,
): Promise<BulkDeleteResult> {
  return apiClient().post(canopyPath('/companies/bulk-delete'), {
    schema: bulkDeleteResultSchema,
    body: { ids },
    ...(signal ? { signal } : {}),
  })
}

export interface CompanyProfileInput {
  productDescription?: string | null
  coreValueProposition?: string | null
  primaryVertical?: string | null
  buyerPersona?: string | null
  aiNativeThreats?: string | null
  coreWorkflows?: readonly string[]
  namedCompetitors?: readonly string[]
  marketKeywords?: readonly string[]
}

export interface CompanyPatch {
  name?: string
  website?: string | null
  sector?: string | null
  serentFund?: string | null
  entryDate?: string | null
  status?: string

  arrBand?: string | null
  emailDomain?: string | null

  aiBudget?: string | null
  aiUsage?: string | null
  governance?: string | null
  monetizing?: string | null
  ebitdaImpact?: string | null
  aiLeader?: string | null
  aiLeaderTitle?: string | null
}

export interface CompanyInput extends CompanyPatch {
  onboardingStep?: number | null
  profile?: CompanyProfileInput
}

export function createCompany(input: CompanyInput, signal?: AbortSignal): Promise<Company> {
  return apiClient().post(canopyPath('/companies'), {
    schema: companySchema,
    body: input,
    ...(signal ? { signal } : {}),
  })
}

export function patchCompany(
  companyId: string,
  input: CompanyPatch,
  signal?: AbortSignal,
): Promise<Company> {
  return apiClient().patch(canopyPath(`/companies/${encodeURIComponent(companyId)}`), {
    schema: companySchema,
    body: input,
    ...(signal ? { signal } : {}),
  })
}

export function putCompanyProfile(
  companyId: string,
  input: CompanyProfileInput,
  signal?: AbortSignal,
): Promise<CompanyProfile> {
  return apiClient().put(canopyPath(`/companies/${encodeURIComponent(companyId)}/profile`), {
    schema: companyProfileSchema,
    body: input,
    ...(signal ? { signal } : {}),
  })
}
